import { Injectable } from '@angular/core';
import { ScriptLoaderService } from './script-loader.service';
import { PaymentService } from './payment.service';
import { PaymentData } from '../models/payment.interface';
import { EmailService, EmailData } from './email.service';
import { environment } from '../../../environments/environment';

declare var FlexPaymentForms: any;

@Injectable({
  providedIn: 'root'
})
export class AlignetPaymentGatewayService {
  private scriptLoader = new ScriptLoaderService();

  constructor(
    private paymentService: PaymentService,
    private emailService: EmailService
  ) {}

  /**
   * Obtiene el token de acceso de Alignet
   */
  async obtenerAccessToken(): Promise<string> {
    const response = await fetch(`${environment.alignet.urls.auth}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "authorize",
        grant_type: "client_credentials",
        audience: environment.alignet.audience,
        client_id: environment.alignet.client_id,
        client_secret: environment.alignet.client_secret,
        scope: "create:token post:charges offline_access"
      })
    });
    
    const data = await response.json();
    return data.access_token;
  }

  /**
   * Obtiene el nonce de Alignet
   */
  async obtenerNonce(token: string): Promise<string> {
    const response = await fetch(`${environment.alignet.urls.auth}/nonce`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        action: "create.nonce",
        audience: environment.alignet.audience,
        client_id: environment.alignet.client_id,
        scope: "post:charges"
      })
    });
    
    const data = await response.json();
    return data.nonce;
  }

  /**
   * Carga el formulario de pago de Alignet
   */
  async cargarFormulario(
    targetElement: HTMLElement,
    loadingElement: HTMLElement,
    monto: number,
    customerData?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      address?: string;
      city?: string;
      state?: string;
      country?: string;
    },
    paymentInfo?: {
      userId?: string;
      planId?: string;
      planName?: string;
      frequency?: string;
      sessionDate?: string;
      sessionTime?: string;
    },
    onPaymentSuccess?: () => void,
    onPaymentError?: () => void
  ): Promise<void> {
    // Mostrar loading y ocultar target
    loadingElement.style.display = "block";
    targetElement.style.display = "none";
    targetElement.innerHTML = "";

    try {
      // Cargar el CSS de FlexPaymentForms
      await this.loadCSS(environment.alignet.urls.css);
      
      // Cargar el script de FlexPaymentForms
      await this.scriptLoader.loadScript(environment.alignet.urls.js);

      // Obtener token y nonce
      const token = await this.obtenerAccessToken();
      const nonce = await this.obtenerNonce(token);

      // Crear el payload del pago
      const payload = {
        action: "authorize",
        channel: "ecommerce",
        merchant_code: environment.alignet.merchant_code,
        merchant_operation_number: Math.floor(Date.now()).toString().substring(7),
        payment_method: {},
        payment_details: {
          amount: monto.toString(),
          currency: "604", // PEN
          billing: {
            first_name: customerData?.firstName || "Cliente",
            last_name: customerData?.lastName || "Cassinelli",
            email: customerData?.email || "cliente@cassinelli.com",
            phone: {
              country_code: "+51",
              subscriber: customerData?.phone || "999999999"
            },
            location: {
              line_1: customerData?.address || "Av. Ejemplo 123",
              line_2: "",
              city: customerData?.city || "Lima",
              state: customerData?.state || "Lima",
              country: customerData?.country || "Peru"
            }
          }
        }
      };

      // Inicializar FlexPaymentForms
      const paymentForm = new FlexPaymentForms({
        nonce,
        payload,
        settings: { show_close_button: false },
        display_settings: {
          //methods: ['CARD', 'YAPE', 'QR', 'BANK_TRANSFER', 'CUOTEALO', 'PAGOEFECTIVO']
          methods: ['CARD', 'YAPE']
        }
      });

      // Inicializar el formulario en el elemento target
      paymentForm.init(
        targetElement,
        (successData: any) => {
          console.log('Pago exitoso:', successData);
          this.handleSuccessfulPayment(successData, paymentInfo, customerData, monto, onPaymentSuccess);
        },
        (errorData: any) => {
          console.log('Error en el pago:', errorData);
          this.handleFailedPayment(errorData, paymentInfo, customerData, monto, onPaymentError);
        },
        (error: any) => {
          console.error('Error crítico:', error);
          this.handleCriticalError(error, paymentInfo, customerData, monto, onPaymentError);
        }
      );

      // Ocultar loading y mostrar formulario
      loadingElement.style.display = "none";
      targetElement.style.display = "block";

    } catch (error) {
      console.error('Error al cargar formulario de Alignet:', error);
      alert("Error al cargar formulario de pago.");
      loadingElement.style.display = "none";
      targetElement.style.display = "block";
    }
  }

  /**
   * Método para limpiar el formulario
   */
  limpiarFormulario(targetElement: HTMLElement): void {
    targetElement.innerHTML = "";
  }

  /**
   * Carga un archivo CSS dinámicamente
   */
  private loadCSS(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verificar si el CSS ya está cargado
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      link.onload = () => resolve();
      link.onerror = () => reject(`Error al cargar el CSS ${href}`);
      document.head.appendChild(link);
    });
  }

  /**
   * Maneja un pago exitoso
   */
  private handleSuccessfulPayment(
    successData: any,
    paymentInfo?: any,
    customerData?: any,
    monto?: number,
    onPaymentSuccess?: () => void
  ): void {
    console.log('paymentInfo', paymentInfo);
    console.log('customerData', customerData);
    const paymentData = {
      userId: paymentInfo?.userId || 'unknown',
      planId: paymentInfo?.planId || 'unknown',
      planName: paymentInfo?.planName || 'Unknown Plan',
      amount: monto || 0,
      currency: 'PEN',
      frequency: paymentInfo?.frequency || 'anual',
      paymentMethod: successData?.payment_method || 'unknown',
      transactionId: successData?.transaction_id || successData?.id,
      alignetData: successData,
      customerData: customerData,
      sessionDate: paymentInfo?.sessionDate,
      sessionTime: paymentInfo?.sessionTime,
      status: 'success' as const,
      createdAt: new Date()
    } as PaymentData;

    this.paymentService.saveSuccessfulPayment(paymentData).subscribe({
      next: (response) => {
        console.log('Pago guardado exitosamente en BD:', response);
        
        // Actualizar el transactionId con el valor correcto del endpoint
        const correctTransactionId = response?.paymentDetails?.alignetData?.transaction?.transaction_id || 
                                   response?.transaction_id || 
                                   paymentData.transactionId;
        
        
        // Actualizar el objeto paymentData con el transactionId correcto
        const updatedPaymentData = {
          ...paymentData,
          transactionId: correctTransactionId,
          meetingLink : `${environment.meetingUrl}${correctTransactionId}`
        };

        console.log('updatedPaymentData', updatedPaymentData);
        
        // Enviar correo de notificación con el transactionId correcto
        this.sendPurchaseNotificationEmail(updatedPaymentData, successData);
        
        // Ejecutar callback después de guardar exitosamente el pago
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
      },
      error: (error) => {
        console.error('Error al guardar pago exitoso en BD:', error);
      }
    });
  }

  /**
   * Maneja un pago fallido
   */
  private handleFailedPayment(
    errorData: any,
    paymentInfo?: any,
    customerData?: any,
    monto?: number,
    onPaymentError?: () => void
  ): void {
    const paymentData = {
      userId: paymentInfo?.userId || 'unknown',
      planId: paymentInfo?.planId || 'unknown',
      planName: paymentInfo?.planName || 'Unknown Plan',
      amount: monto || 0,
      currency: 'PEN',
      frequency: paymentInfo?.frequency || 'anual',
      alignetData: errorData,
      customerData: customerData,
      errorMessage: errorData?.message || errorData?.error || 'Error desconocido en el pago',
      status: 'error' as const,
      createdAt: new Date()
    } as PaymentData;

    this.paymentService.saveFailedPayment(paymentData).subscribe({
      next: (response) => {
        console.log('Pago fallido guardado en BD:', response);
        // Ejecutar callback de error después de guardar el pago fallido
        if (onPaymentError) {
          onPaymentError();
        }
      },
      error: (error) => {
        console.error('Error al guardar pago fallido en BD:', error);
        // Ejecutar callback de error incluso si falla al guardar en BD
        if (onPaymentError) {
          onPaymentError();
        }
      }
    });
  }

  /**
   * Maneja errores críticos
   */
  private handleCriticalError(
    error: any,
    paymentInfo?: any,
    customerData?: any,
    monto?: number,
    onPaymentError?: () => void
  ): void {
    const paymentData = {
      userId: paymentInfo?.userId || 'unknown',
      planId: paymentInfo?.planId || 'unknown',
      planName: paymentInfo?.planName || 'Unknown Plan',
      amount: monto || 0,
      currency: 'PEN',
      frequency: paymentInfo?.frequency || 'anual',
      alignetData: error,
      customerData: customerData,
      errorMessage: error?.message || error?.error || 'Error crítico en el sistema de pagos',
      status: 'error' as const,
      createdAt: new Date()
    } as PaymentData;

    this.paymentService.saveFailedPayment(paymentData).subscribe({
      next: (response) => {
        console.log('Error crítico guardado en BD:', response);
        // Ejecutar callback de error después de guardar el error crítico
        if (onPaymentError) {
          onPaymentError();
        }
      },
      error: (dbError) => {
        console.error('Error al guardar error crítico en BD:', dbError);
        // Ejecutar callback de error incluso si falla al guardar en BD
        if (onPaymentError) {
          onPaymentError();
        }
      }
    });
  }

  /**
   * Envía correo de notificación de compra exitosa
   */
  private sendPurchaseNotificationEmail(paymentData: PaymentData, successData: any): void {
    // Solo enviar correo si es una sesión con mentor/experto
    if (paymentData.planId === 'mentor-session' || paymentData.planId === 'experto-session') {
      console.log('paymentData', paymentData);
      console.log('successData', successData);
      const emailData: EmailData = {
        to: paymentData.customerData?.email || '',
        subject: 'Confirmación de compra - Phoenix',
        template: 'purchase-notification',
        data: {
          clientName: paymentData.customerData?.firstName || 'Cliente',
          mentorName: paymentData.planName || 'Mentor',
          amount: paymentData.amount || 0,
          currency: paymentData.currency || 'PEN',
          transactionId: paymentData.transactionId || successData?.transaction_id || 'N/A',
          meetingLink: paymentData.meetingLink || 'N/A',
          sessionDate: (paymentData as any)?.sessionDate || 'Por confirmar',
          sessionTime: (paymentData as any)?.sessionTime || 'Por confirmar'
        }
      };

      this.emailService.sendPurchaseNotification(emailData).subscribe({
        next: (response) => {
          console.log('Correo de notificación enviado exitosamente:', response);
        },
        error: (error) => {
          console.error('Error al enviar correo de notificación:', error);
        }
      });
    }
  }
}
